import { PRODUCTS, useProducts, type ProductKey } from '../context/ProductContext.js';
import { cn } from '../components/ui/cn.js';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';

const COLOR_MAP: Record<string, { active: string; ring: string }> = {
  blue:   { active: 'bg-blue-50 border-blue-300',    ring: 'ring-blue-500/20'   },
  teal:   { active: 'bg-teal-50 border-teal-300',    ring: 'ring-teal-500/20'   },
  violet: { active: 'bg-violet-50 border-violet-300', ring: 'ring-violet-500/20' },
};

export function SettingsPage() {
  const { selected, addProduct, removeProduct } = useProducts();

  function toggleProduct(key: ProductKey) {
    if (selected.includes(key)) {
      if (selected.length === 1) {
        toast.error('You must keep at least one product selected.');
        return;
      }
      removeProduct(key);
      toast.success('Product removed.');
    } else {
      addProduct(key);
      toast.success('Product added.');
    }
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-8">
        <h1 className="text-xl font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage which products are active in your portal.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-800">Active Products</h2>
          <p className="text-xs text-slate-400 mt-0.5">At least one product must remain active.</p>
        </div>

        <div className="divide-y divide-slate-50">
          {PRODUCTS.map((p) => {
            const isActive = selected.includes(p.key);
            const c = COLOR_MAP[p.color];
            return (
              <div
                key={p.key}
                className={cn(
                  'flex items-center gap-4 px-5 py-4 transition-all',
                  isActive ? cn(c.active, 'ring-inset ring-1', c.ring) : 'bg-white',
                )}
              >
                <span className="text-xl select-none w-8 text-center">{p.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{p.label}</p>
                    {isActive
                      ? <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">Active</span>
                      : <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">Inactive</span>
                    }
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{p.subtitle}</p>
                </div>
                <button
                  type="button"
                  onClick={() => toggleProduct(p.key)}
                  className={cn(
                    'inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all shrink-0',
                    isActive
                      ? 'bg-red-50 text-red-600 hover:bg-red-100'
                      : 'bg-blue-600 text-white hover:bg-blue-700',
                  )}
                >
                  {isActive
                    ? <><XCircle className="w-3.5 h-3.5" /> Remove</>
                    : <><CheckCircle2 className="w-3.5 h-3.5" /> Add</>
                  }
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
